/**
 * voiceStateMachine — the authoritative state machine for the KALKI voice
 * conversation. Pure functions, zero side effects. Easy to test with Jest.
 *
 * One voice. One question. One answer. One peaceful conversation.
 *
 * Primary (mutually exclusive) states:
 *   IDLE                — before CALL is tapped
 *   CONNECTING          — mic & audio warming up
 *   READY               — session connected, waiting for user to tap SPEAK or speak
 *   USER_SPEAKING       — the mic is capturing a real user utterance
 *   USER_TURN_FINALIZING — endpoint detected, audio being committed
 *   TRANSCRIBING        — STT in flight
 *   KRISHNA_THINKING    — LLM in flight
 *   KRISHNA_SPEAKING    — TTS playing on-device
 *   INTERRUPTED         — user barged in / stopped Krishna
 *   ERROR               — recoverable fault
 *   ENDING              — tearing down the session
 *
 * Invariants (enforced by `canTransition`):
 *   • USER_SPEAKING and KRISHNA_SPEAKING are never active together.
 *   • Only one turn is active per session.
 *   • A new turn can only start from READY (controlled mode) or from
 *     KRISHNA_SPEAKING→READY after post-playback settle (hands-free).
 *   • ENDING is terminal until a brand new session is started.
 */

export const VoiceState = Object.freeze({
  IDLE: "idle",
  CONNECTING: "connecting",
  READY: "ready",
  // Legacy alias — TalkKrishna.jsx still reads LISTENING. Keep both until the
  // UI migration is complete, pointing at the new USER_SPEAKING.
  LISTENING: "user_speaking",
  USER_SPEAKING: "user_speaking",
  USER_TURN_FINALIZING: "user_turn_finalizing",
  TRANSCRIBING: "transcribing",
  THINKING: "krishna_thinking",       // legacy alias
  KRISHNA_THINKING: "krishna_thinking",
  PREPARING_AUDIO: "krishna_thinking", // legacy alias — the TTS network window
  KRISHNA_SPEAKING: "krishna_speaking",
  SPEAKING: "krishna_speaking",       // legacy alias
  INTERRUPTED: "interrupted",
  ERROR: "error",
  ENDING: "ending",
});

export const ConversationMode = Object.freeze({
  CONTROLLED: "controlled",
  HANDS_FREE: "handsfree",
});

/**
 * Allowed transitions. Each entry maps a source → set of allowed targets.
 * `canTransition` is the single gate the controller must use for any state
 * change. Any mutation that is not listed here is a bug.
 */
const ALLOWED = {
  idle: ["connecting", "ending"],
  connecting: ["ready", "error", "ending"],
  ready: ["user_speaking", "ending", "error"],
  user_speaking: ["user_turn_finalizing", "ready", "error", "ending", "interrupted"],
  user_turn_finalizing: ["transcribing", "ready", "error", "ending"],
  transcribing: ["krishna_thinking", "ready", "error", "ending"],
  krishna_thinking: ["krishna_speaking", "ready", "error", "ending"],
  krishna_speaking: ["ready", "interrupted", "error", "ending"],
  interrupted: ["ready", "ending"],
  error: ["ready", "idle", "ending"],
  ending: ["idle"],
};

export function canTransition(from, to) {
  const list = ALLOWED[from];
  return Array.isArray(list) && list.includes(to);
}

/**
 * The brief's starting endpoint-detection tuning. These are starting values
 * and may be tuned per-device after real-world capture.
 */
export const ENDPOINT = Object.freeze({
  SPEECH_ONSET_MS: 250,              // speech must be heard this long before onset
  MIN_UTTERANCE_MS: 500,             // reject anything shorter than this
  END_OF_SPEECH_SILENCE_MS: 1800,    // continuous silence that ends a turn
  SHORT_PAUSE_TOLERANCE_MS: 1500,    // mid-sentence pause that is NOT a turn end
  POST_PLAYBACK_SETTLE_MS: 400,      // settle after Krishna finishes
  MAX_UTTERANCE_MS: 60000,           // safety cap on a single utterance (1 min)
  VAD_LEVEL_THRESHOLD: 0.06,         // 0..1 analyser amplitude considered "speech"
});

/**
 * A monotonically ordered pair of session + turn tokens. Any asynchronous
 * callback must verify its token still matches the live session/turn before
 * mutating state. This is the ONLY way to defend against stale STT/LLM/TTS
 * responses from a previous call.
 */
export function makeSessionId() {
  return `sess_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}
export function makeTurnId(sessionId, index) {
  return `${sessionId}_turn_${index}`;
}

/**
 * Guard: given a session+turn and the controller's live session+turn, return
 * true iff this callback is still valid.
 */
export function isLive(callbackSessionId, callbackTurnId, liveSessionId, liveTurnId) {
  return !!callbackSessionId
    && callbackSessionId === liveSessionId
    && (callbackTurnId == null || callbackTurnId === liveTurnId);
}

/**
 * Decide what the user-facing CALL/CONTEXT button should do next, given the
 * current state. This matches the brief's "Smart contextual button" contract.
 *   IDLE / ERROR / ENDING → "call"       (start session)
 *   READY / INTERRUPTED   → "speak"      (begin next turn)
 *   USER_SPEAKING         → "done"       (finalize current utterance)
 *   KRISHNA_THINKING      → "thinking"   (disabled / no-op)
 *   KRISHNA_SPEAKING      → "interrupt"  (barge-in)
 *   CONNECTING            → "wait"       (disabled)
 *   USER_TURN_FINALIZING / TRANSCRIBING → "wait"
 */
export function callButtonIntent(state) {
  switch (state) {
    case "idle":
    case "error":
    case "ending":
      return "call";
    case "ready":
    case "interrupted":
      return "speak";
    case "user_speaking":
      return "done";
    case "krishna_speaking":
      return "interrupt";
    case "krishna_thinking":
    case "connecting":
    case "user_turn_finalizing":
    case "transcribing":
      return "wait";
    default:
      return "wait";
  }
}

/**
 * Human label for the contextual button. Short and clear — the Talk UI uses
 * `callButtonLabel(state)` directly.
 */
export function callButtonLabel(intent) {
  return {
    call: "CALL",
    speak: "SPEAK",
    done: "DONE",
    interrupt: "INTERRUPT",
    wait: "…",
  }[intent] || "CALL";
}

/**
 * Should the controller auto-restart listening after Krishna finishes?
 * Controlled mode = NO (default). Hands-free = YES, after the settle delay.
 */
export function shouldAutoRestartAfterKrishna(mode) {
  // Voice 6.0 — hands-free auto-restart is permanently disabled. The ONLY
  // way to open a new user turn is an explicit press-and-hold on the mic.
  // Keeping the signature + unused param so the hook keeps compiling
  // without a cascade of edits.
  void mode;
  return false;
}
